import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { DashboardShell } from "@/components/DashboardShell";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { FieldRenderer } from "@/components/FieldRenderer";
import { FileUploadField } from "@/components/FileUploadField";
import { SignaturePad } from "@/components/SignaturePad";
import { EquipmentTypeCards } from "@/components/EquipmentTypeCards";
import { EquipmentTypeBadge } from "@/components/EquipmentTypeBadge";
import { FmcsaLookupCard } from "@/components/FmcsaLookupCard";
import { FilePreviewModal } from "@/components/FilePreviewModal";
import { UploadStatusBadge } from "@/components/UploadStatusBadge";
import api, { formatApiError } from "@/lib/api";
import { formatErrorSummaryLabel, formatSavedFailedSubline } from "@/lib/formErrorSummary";
import { resolveSubmissionEditability } from "@/lib/statusResolvers";  // Phase STATE-DRIFT-SWEEP
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Save, Send, CheckCircle2, AlertCircle, Download, RefreshCcw, AlertTriangle, X, Info, Eye } from "lucide-react";

const STEPS = [
    { key: "equipment", label: "Equipment" },
    { key: "company", label: "Company" },
    { key: "guarantor", label: "Guarantor" },
    { key: "insurance_agent", label: "Insurance Agent" },
    { key: "references", label: "References" },
    { key: "uploads", label: "Uploads" },
    { key: "signatures", label: "Signatures" },
    { key: "review", label: "Review & submit" },
];

const GROUP_BY_STEP = { equipment: "equipment", company: "company", guarantor: "guarantor", insurance_agent: "insurance_agent", references: "references", uploads: "uploads", signatures: "signatures" };

function isFilledValue(v) {
    if (v == null) return false;
    if (typeof v === "string") return v.trim() !== "";
    if (typeof v === "object") return Object.values(v).some(isFilledValue);
    return true;
}

export default function SubmissionFormPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [catalog, setCatalog] = useState(null);
    const [submission, setSubmission] = useState(null);
    const [data, setData] = useState({});
    const [stepIdx, setStepIdx] = useState(0);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [submitErrors, setSubmitErrors] = useState([]);
    // Task 26-B — track whether the user has attempted submit, so we
    // can show per-field inline errors on the uploads step (and any
    // future required field) until they fix them.
    const [submitAttempted, setSubmitAttempted] = useState(false);
    // Inline per-field errors keyed by field_key. Populated from the
    // backend's 400 PATCH response (detail.field_errors). Cleared per-key
    // when the user starts editing that input, and cleared wholesale on a
    // successful save.
    const [fieldErrors, setFieldErrors] = useState({});
    // Snapshot of `saved_keys` from the most recent partial-save 400 —
    // the field keys whose values WERE persisted on that same PATCH.
    // Drives the "{N} saved, {M} failed" sub-line under the error
    // summary banner so customers don't panic that they lost work.
    // `null` means "no recent failed save / payload didn't carry the
    // array" — banner omits the sub-line in that case.
    const [savedKeysSnapshot, setSavedKeysSnapshot] = useState(null);
    const [prefillInfo, setPrefillInfo] = useState(null);
    const [prefillBannerHidden, setPrefillBannerHidden] = useState(false);
    const [applyingPrefill, setApplyingPrefill] = useState(false);
    // Phase DP — customer-side read-only preview modal.
    const [previewFile, setPreviewFile] = useState(null);
    const previewOpen = !!previewFile;
    const dirtyRef = useRef(false);
    const debounceTimerRef = useRef(null);

    // Phase DRYRUN (2026-06-29) — server-truth validator state for Step 8.
    // Source of truth is /api/customer/submissions/{id}/dry-run-validate,
    // which shares its core logic with /submit. We still keep the FE-local
    // ``requiredMissing`` heuristic as a fallback when the network is down
    // (graceful degrade — see ``dryRun.offline``).
    const [dryRun, setDryRun] = useState(null);
    const dryRunTimerRef = useRef(null);

    // Load catalog + submission + prefill info
    useEffect(() => {
        let active = true;
        (async () => {
            try {
                const [catRes, subRes, preRes] = await Promise.all([
                    api.get("/fields/catalog"),
                    api.get(`/submissions/${id}`),
                    api.get(`/submissions/${id}/prefill-info`).catch(() => ({ data: { available: false } })),
                ]);
                if (!active) return;
                setCatalog(catRes.data);
                setSubmission(subRes.data);
                setData(subRes.data.data || {});
                setPrefillInfo(preRes.data || null);
            } catch (err) {
                if (active) setError(formatApiError(err));
            } finally {
                if (active) setLoading(false);
            }
        })();
        return () => {
            active = false;
        };
    }, [id]);

    const isLocked = submission?.status === "submitted";
    // Phase STATE-DRIFT-SWEEP — broader read-only gate for every
    // non-draft terminal state (approved / equipment_assigned /
    // cancelled / rejected / awaiting_reassignment). ``isLocked`` above
    // stays narrow (drives the "Application submitted" banner copy),
    // while ``isReadOnly`` gates every save/upload/field disable.
    const editability = submission
        ? resolveSubmissionEditability(submission)
        : { locked: false, reason: "" };
    const isReadOnly = isLocked || editability.locked;
    const isTerminalReadOnly = editability.locked && !isLocked;
    const cfg = submission?.field_config || {};

    const fieldsByGroup = useMemo(() => {
        if (!catalog) return {};
        const out = {};
        for (const f of catalog.fields) {
            if (cfg[f.key] === "hidden") continue;
            (out[f.group] = out[f.group] || []).push(f);
        }
        return out;
    }, [catalog, cfg]);

    const uploadsByKey = useMemo(() => {
        const map = {};
        for (const u of submission?.uploads || []) map[u.field_key] = u;
        return map;
    }, [submission]);

    const signaturesByKey = useMemo(() => {
        const map = {};
        for (const s of submission?.signatures || []) map[s.field_key] = s;
        return map;
    }, [submission]);

    const requiredMissing = useMemo(() => {
        if (!catalog) return [];
        // HOTFIX (2026-08-01) — the FE fallback now honors ``requires_when``
        // conditionals (same rule the server-side validator applies). Prior
        // to this, a conditional field like ``equipment_type_other`` was
        // treated as unconditionally required whenever the invite cfg
        // said ``required``, which surfaced a spurious "Missing Specify
        // Equipment Type" error when the customer picked "Reefer" and
        // the dry-run endpoint was momentarily offline.
        const TRUTHY = new Set([true, "true", "True", "yes", "Yes", "YES", 1, "1"]);
        const isActive = (f) => {
            const rule = f.requires_when
                || (f.requires_toggle
                    ? { key: f.requires_toggle, in: [...TRUTHY] }
                    : null);
            if (!rule) return true;
            const val = data[rule.key];
            const allowed = rule.in || [];
            const nullInactive = !!rule.null_means_inactive;
            if (val == null) return !nullInactive;
            return allowed.some((a) => a === val);
        };
        const missing = [];
        for (const f of catalog.fields) {
            if (cfg[f.key] !== "required") continue;
            if (!isActive(f)) continue;  // HOTFIX — skip when conditional off
            if (f.type === "file") {
                if (!uploadsByKey[f.key]) missing.push(f);
            } else if (f.type === "signature") {
                if (!signaturesByKey[f.key]) missing.push(f);
            } else {
                if (!isFilledValue(data[f.key])) missing.push(f);
            }
        }
        return missing;
    }, [catalog, cfg, data, uploadsByKey, signaturesByKey]);

    // Phase DRYRUN — server-side validation. Fires when on Step 8 and on
    // any debounced data change (200ms). Sends `overrides` so the server
    // sees the customer's unsaved keystrokes; no PATCH-write needed.
    const stepKey = STEPS[stepIdx]?.key;
    useEffect(() => {
        if (!submission || isReadOnly) return;
        if (stepKey !== "review") return;
        if (dryRunTimerRef.current) clearTimeout(dryRunTimerRef.current);
        dryRunTimerRef.current = setTimeout(async () => {
            try {
                const persisted = submission?.data || {};
                const overrides = {};
                for (const k of Object.keys(data || {})) {
                    if (data[k] !== persisted[k]) overrides[k] = data[k];
                }
                const { data: out } = await api.post(
                    `/customer/submissions/${id}/dry-run-validate`,
                    Object.keys(overrides).length ? { overrides } : {},
                );
                setDryRun({ ...out, offline: false });
            } catch (_) {
                // Graceful degrade — keep last good result; mark offline so
                // the FE can show a warning banner. Submit gate falls back
                // to the FE-local heuristic.
                setDryRun((prev) => prev ? { ...prev, offline: true } : { offline: true });
            }
        }, 200);
        return () => {
            if (dryRunTimerRef.current) clearTimeout(dryRunTimerRef.current);
        };
    }, [stepKey, submission, isLocked, data, uploadsByKey, signaturesByKey, id]);

    // What Step-8 actually shows. Server truth (dryRun) wins; FE-local
    // ``requiredMissing`` is the fallback when offline / pre-first-call.
    const serverOk = dryRun?.ok === true;
    const useServerSummary = dryRun && !dryRun.offline && Array.isArray(dryRun.missing_required);
    const displayMissingCount = useServerSummary
        ? dryRun.missing_required.length + Object.keys(dryRun.field_errors || {}).length
        : requiredMissing.length;
    const displayMissingFields = useServerSummary
        ? [
            ...(dryRun.missing_required || []).map((k) => {
                const f = catalog?.fields.find((x) => x.key === k);
                return f || { key: k, label: k };
            }),
            ...Object.entries(dryRun.field_errors || {}).map(([k, msg]) => {
                const f = catalog?.fields.find((x) => x.key === k);
                return { ...(f || { key: k, label: k }), errorMessage: msg };
            }),
        ]
        : requiredMissing;

    // Task 26-B — fast lookup of missing required upload keys for the
    // uploads step inline-error UI. Only populated after the customer
    // has tried to submit at least once; until then we don't shout at
    // them with red borders before they've had a chance to fill the
    // form.
    const missingUploadKeys = useMemo(() => {
        if (!submitAttempted) return new Set();
        const keys = new Set();
        for (const f of requiredMissing) {
            if (f.type === "file") keys.add(f.key);
        }
        return keys;
    }, [requiredMissing, submitAttempted]);

    const saveDraft = useCallback(
        async (silent = false) => {
            // Phase P0-HOTFIX2 (2026-08-01) — returns a result envelope so
            // callers (esp. onSubmitFinal) can distinguish success /
            // partial-save / terminal failure and stop swallowing silent
            // network errors that led to submit races.
            if (!submission || isReadOnly) return { ok: true, data: submission?.data || {} };
            if (!dirtyRef.current) return { ok: true, data: submission?.data || {} };
            setSaving(true);
            try {
                const { data: fresh } = await api.patch(`/submissions/${id}`, { data });
                setSubmission(fresh);
                setData(fresh.data || {});
                dirtyRef.current = false;
                // Successful save → wipe all inline errors. Anything still
                // unsaved will resurface on the next PATCH attempt.
                setFieldErrors({});
                setSavedKeysSnapshot(null);
                if (!silent) toast.success("Draft saved");
                return { ok: true, data: fresh.data || {} };
            } catch (err) {
                // Partial-save bug fix (Step 8 "23 fields missing" incident):
                // server now returns 400 with structured detail containing
                // `field_errors` + `saved_keys` when some fields validated
                // and others didn't. The valid fields ARE persisted; the
                // bad ones are not. Refresh local state so we never display
                // typed data that didn't actually save, and surface the
                // per-field errors so the customer can correct them.
                const detail = err?.response?.data?.detail;
                if (detail && typeof detail === "object" && detail.field_errors) {
                    // Push the per-field errors into local state so the
                    // form renders them inline under each affected input.
                    setFieldErrors(detail.field_errors);
                    // Capture saved_keys (defensive — old payloads may
                    // omit it; helper treats non-array as "absent").
                    setSavedKeysSnapshot(
                        Array.isArray(detail.saved_keys) ? detail.saved_keys : null
                    );
                    // 2026-06-23 fix (MATE MOTORS LLC "13 fields missing"
                    // incident): when the backend partial-rejects a PATCH
                    // we used to refresh ``data`` wholesale from the
                    // server — which silently DROPPED the customer's
                    // typed values for the rejected keys (e.g. a phone
                    // they entered as ``215-555-1234`` that failed
                    // normalization). The customer would then navigate
                    // to Step 8 and see those fields flagged as missing
                    // even though they "filled them in".
                    //
                    // Fix: keep the customer's locally-typed values for
                    // the rejected keys (they're what the inline error
                    // message is pointing at — the customer NEEDS to see
                    // them to correct the format) and only sync server
                    // values for keys the customer is NOT actively
                    // correcting.
                    const rejectedKeys = new Set(Object.keys(detail.field_errors));
                    let mergedData = { ...data };
                    try {
                        const { data: fresh } = await api.get(`/submissions/${id}`);
                        setSubmission(fresh);
                        setData((current) => {
                            // Server is the source of truth for everything
                            // EXCEPT the rejected keys — for those, the
                            // server has the OLD value (pre-edit) and the
                            // customer's NEW value lives only in local
                            // state. Merge so they see + can fix it.
                            const merged = { ...(fresh.data || {}) };
                            for (const k of rejectedKeys) {
                                if (k in current) merged[k] = current[k];
                            }
                            mergedData = merged;
                            return merged;
                        });
                        dirtyRef.current = rejectedKeys.size > 0;
                    } catch {
                        // GET failed — keep local state, customer can retry
                    }
                    const badKeys = Object.keys(detail.field_errors);
                    const firstMsg = detail.field_errors[badKeys[0]];
                    toast.error(
                        badKeys.length === 1
                            ? `${badKeys[0]}: ${firstMsg}`
                            : `${badKeys.length} fields need attention — first: ${badKeys[0]}: ${firstMsg}`
                    );
                    return {
                        ok: false,
                        kind: "field_errors",
                        data: mergedData,
                        field_errors: detail.field_errors,
                    };
                } else if (!silent) {
                    toast.error(formatApiError(err));
                }
                return {
                    ok: false,
                    kind: "network",
                    data: data || {},
                    error: err?.message || "save_failed",
                };
            } finally {
                setSaving(false);
            }
        },
        [data, id, isLocked, submission]
    );

    // Debounced auto-save on data change
    useEffect(() => {
        if (!submission || isReadOnly) return;
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = setTimeout(() => {
            if (dirtyRef.current) saveDraft(true);
        }, 20000);
        return () => debounceTimerRef.current && clearTimeout(debounceTimerRef.current);
    }, [data, saveDraft, isReadOnly, submission]);

    const setField = (key, val) => {
        dirtyRef.current = true;
        setData((d) => ({ ...d, [key]: val }));
        // Clear this field's inline error as soon as the user starts
        // editing it — they're actively correcting; the stale message
        // shouldn't keep shouting at them.
        setFieldErrors((errs) => {
            if (!(key in errs)) return errs;
            const { [key]: _drop, ...rest } = errs;
            return rest;
        });
    };

    const refreshSubmission = async () => {
        const { data: fresh } = await api.get(`/submissions/${id}`);
        setSubmission(fresh);
        setData(fresh.data || {});
    };

    const onUploadChange = async () => {
        await refreshSubmission();
    };

    const onUsePrevious = async () => {
        setApplyingPrefill(true);
        try {
            const { data: result } = await api.post(`/submissions/${id}/use-prefill`);
            if (result?.submission) {
                setSubmission(result.submission);
                setData(result.submission.data || {});
                dirtyRef.current = false;
                if ((result.skipped_uploads || []).includes("coi_upload")) {
                    toast.warning("Previous COI is older than 12 months — please re-upload.");
                } else {
                    toast.success("Previous info applied to this application");
                }
                setPrefillBannerHidden(true);
                // Refresh prefill info to reflect must_replace flags
                try {
                    const { data: pi } = await api.get(`/submissions/${id}/prefill-info`);
                    setPrefillInfo(pi);
                } catch { /* prefill-info refresh failure is non-fatal */ }
            }
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setApplyingPrefill(false);
        }
    };

    const onStartFresh = () => {
        setPrefillBannerHidden(true);
    };

    const goStep = async (newIdx, opts = {}) => {
        if (newIdx < 0 || newIdx >= STEPS.length) return;
        if (!isLocked) await saveDraft(true);

        // Phase ADVGUARD (2026-06-30) — block forward-Next on current-step
        // field errors. Backward jumps and stepper-sidebar clicks (in either
        // direction) are NOT gated — users can always navigate back to fix
        // earlier mistakes. Only the dedicated Next button (gated=true)
        // blocks forward motion when current-step inputs are invalid.
        if (opts.gated && newIdx > stepIdx) {
            const currentGroup = GROUP_BY_STEP[STEPS[stepIdx].key];
            const currentStepKeys = new Set(
                (fieldsByGroup[currentGroup] || []).map((f) => f.key),
            );
            const errorKey = Object.keys(fieldErrors).find((k) =>
                currentStepKeys.has(k),
            );
            if (errorKey) {
                toast.error("Fix the highlighted field before continuing");
                // Focus the offending input — FieldRenderer sets the input
                // ``id`` attribute to the field.key value directly.
                const el = document.getElementById(errorKey);
                if (el) {
                    el.scrollIntoView({ behavior: "smooth", block: "center" });
                    setTimeout(() => { try { el.focus(); } catch { /* noop */ } }, 350);
                }
                return;
            }
        }

        setStepIdx(newIdx);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const onSubmitFinal = async () => {
        // Task 26-B — turn on per-field inline errors as soon as the
        // user tries to submit. The summary toast also flips from
        // generic to count-based.
        setSubmitAttempted(true);
        // Task 26-B — count required uploads missing so we can show a
        // specific summary toast BEFORE round-tripping the server.
        const missingUploads = requiredMissing.filter((f) => f.type === "file");
        if (missingUploads.length > 0) {
            toast.error(
                `${missingUploads.length} required upload${missingUploads.length === 1 ? "" : "s"} missing — see the highlighted boxes`,
            );
            // Snap the wizard to the uploads step so the customer sees
            // the inline red borders without having to backtrack.
            const uploadsStepIdx = STEPS.findIndex((s) => s.key === "uploads");
            if (uploadsStepIdx >= 0 && uploadsStepIdx !== stepIdx) setStepIdx(uploadsStepIdx);
            return;
        }
        setSubmitting(true);
        setSubmitErrors([]);
        try {
            // Phase P0-HOTFIX2b (2026-08-02) — pre-submit save race fix,
            // revised sequence per user feedback: Fix 1 (retry+abort) was
            // masking Fix 2 (submit-with-overrides). Correct sequence:
            //   1. Try saveDraft(silent=true).
            //   2. ok → POST /submit with {data: currentLocalData} (always).
            //   3. !ok && field_errors → surface + cancel (legit blocking
            //      errors — do NOT try overrides fallback).
            //   4. !ok && network/500/auth → TRY POST /submit with
            //      {data: currentLocalData} as recovery. Server persists
            //      the overrides + validates the merged state, and emits
            //      a `submission.submit_recovered_via_overrides` audit
            //      event when the ``X-Save-Race-Recovery: 1`` header is
            //      present.
            //   5. Recovery also fails → NOW show the actionable toast +
            //      cancel (don't leave the customer wondering).
            let saveResult = await saveDraft(true);
            if (!saveResult?.ok && saveResult?.kind !== "field_errors") {
                // one retry before we consider it a hard save-failure
                saveResult = await saveDraft(true);
            }
            if (!saveResult?.ok && saveResult?.kind === "field_errors") {
                // Legit per-field validation errors — do NOT try /submit;
                // the customer must fix them first.
                toast.error("Fix the highlighted fields before submitting.");
                return;
            }
            // Prefer freshly-saved server data; fall back to local ``data``
            // (which is what the customer sees on the Review card) when
            // save failed. This is the "belt-and-suspenders" override
            // that closes the race even if the client's PATCH never
            // landed on the server.
            const overrideData =
                (saveResult?.ok && saveResult?.data) ||
                data ||
                {};
            const overridePayload = Object.keys(overrideData).length
                ? { data: overrideData }
                : {};
            // Flag recovery submits so the BE can distinguish an audit
            // trail row for save-race recovery vs happy-path submit.
            const submitConfig = saveResult?.ok
                ? undefined
                : { headers: { "X-Save-Race-Recovery": "1" } };
            try {
                const { data: finalSub } = await api.post(
                    `/submissions/${id}/submit`,
                    overridePayload,
                    submitConfig,
                );
                setSubmission(finalSub);
                toast.success("Application submitted!");
                setStepIdx(STEPS.length - 1);
                return;
            } catch (submitErr) {
                // If saveDraft ALSO failed AND /submit failed → this is
                // the terminal case: no connectivity or persistent server
                // error. Show the actionable toast + cancel.
                if (!saveResult?.ok) {
                    toast.error(
                        "We couldn't save your latest changes. Please check your connection and try again — we didn't submit your application to avoid losing your edits.",
                    );
                    return;
                }
                // saveDraft succeeded but /submit itself failed → fall
                // through to the outer catch which handles submit-time
                // validation errors (the historical Step-8 error path).
                throw submitErr;
            }
        } catch (err) {
            const detail = err?.response?.data?.detail;
            if (detail && typeof detail === "object" && Array.isArray(detail.errors)) {
                setSubmitErrors(detail.errors);
            } else {
                toast.error(formatApiError(err));
            }
        } finally {
            setSubmitting(false);
        }
    };

    const onSaveExit = async () => {
        await saveDraft(false);
        navigate("/customer");
    };

    if (loading) {
        return (
            <DashboardShell>
                <p className="text-sm text-slate-500" data-testid="submission-loading">Loading your application…</p>
            </DashboardShell>
        );
    }
    if (error || !submission) {
        return (
            <DashboardShell>
                <div className="rounded-md bg-red-50 border border-red-100 p-4 text-sm text-red-700" data-testid="submission-error">
                    {error || "Submission not found"}
                </div>
                <Button asChild variant="outline" className="mt-4">
                    <Link to="/customer">Back to dashboard</Link>
                </Button>
            </DashboardShell>
        );
    }

    const currentStep = STEPS[stepIdx];
    const groupKey = GROUP_BY_STEP[currentStep.key];

    // Inline error summary banner (Task 1) — surfaces a count + a
    // "Jump to first" affordance when the backend returned per-field
    // validation errors. Stays purely local to this page so we don't
    // need a global error context for one banner.
    const errorKeys = Object.keys(fieldErrors);
    const errorCount = errorKeys.length;

    const jumpToFirstError = () => {
        const firstKey = errorKeys[0];
        if (!firstKey) return;
        const field = catalog?.fields.find((f) => f.key === firstKey);
        const targetStepIdx = field
            ? STEPS.findIndex((s) => GROUP_BY_STEP[s.key] === field.group)
            : -1;
        const doScrollAndFocus = () => {
            const row = document.querySelector(`[data-testid="field-row-${firstKey}"]`);
            if (row && typeof row.scrollIntoView === "function") {
                row.scrollIntoView({ behavior: "smooth", block: "center" });
            }
            const input = document.getElementById(firstKey);
            if (input && typeof input.focus === "function") {
                // Slight delay so the smooth scroll has started before
                // the browser's focus jump fights it.
                setTimeout(() => input.focus({ preventScroll: true }), 50);
            }
        };
        if (targetStepIdx >= 0 && targetStepIdx !== stepIdx) {
            // Switch step first; defer scroll until next paint so the
            // target row is actually in the DOM.
            setStepIdx(targetStepIdx);
            setTimeout(doScrollAndFocus, 150);
        } else {
            doScrollAndFocus();
        }
    };

    const renderFieldRow = (f) => {
        const required = cfg[f.key] === "required";
        const inlineError = fieldErrors[f.key];
        const errorId = inlineError ? `field-error-${f.key}` : undefined;
        // "Required by your rep" badge (Task 2) — admin flipped a
        // normally-optional field to required on this invite. Catalog
        // is the source of truth for `default_required` (same flag
        // CreateInvitePage reads for its admin-side warning chip).
        const overConstrained = required && f.default_required === false;
        const repBadge = overConstrained ? (
            <span
                data-testid={`rep-required-badge-${f.key}`}
                title="Your rep marked this field as required for your application."
                className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-medium text-blue-800 ring-1 ring-blue-200"
            >
                <Info size={10} className="shrink-0" />
                Required by your rep
            </span>
        ) : null;
        return (
            <div key={f.key} className="space-y-2" data-testid={`field-row-${f.key}`}>
                {f.type !== "composite" ? (
                    <div className="flex items-center gap-2 flex-wrap">
                        <Label htmlFor={f.key}>
                            {f.label}
                            {required && <span className="text-red-600 ml-1">*</span>}
                        </Label>
                        {repBadge}
                    </div>
                ) : (
                    repBadge && <div>{repBadge}</div>
                )}
                <FieldRenderer
                    field={f}
                    value={data[f.key]}
                    onChange={(v) => setField(f.key, v)}
                    disabled={isReadOnly}
                    error={inlineError}
                    errorId={errorId}
                />
                {inlineError && (
                    <p
                        id={errorId}
                        role="alert"
                        className="text-xs text-red-600 mt-1"
                        data-testid={`field-error-${f.key}`}
                    >
                        {inlineError}
                    </p>
                )}
                {f.key === "usdot_number" && !isLocked && (
                    <FmcsaLookupCard
                        usdot={data[f.key] || ""}
                        companyValues={{
                            company_name: data.company_name || "",
                            company_address: data.company_address || "",
                            company_phone: data.company_phone || "",
                            ein_tax_id: data.ein_tax_id || "",
                        }}
                        onPrefill={(patch) => {
                            setData((d) => ({ ...d, ...patch }));
                            dirtyRef.current = true;
                        }}
                        testIdPrefix="form-fmcsa"
                    />
                )}
            </div>
        );
    };

    return (
        <DashboardShell>
            <div className="flex items-start justify-between flex-wrap gap-3 fade-up">
                <div>
                    <Link to="/customer" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 mb-3">
                        <ChevronLeft size={14} /> My submissions
                    </Link>
                    <p className="text-xs font-medium tracking-[0.18em] uppercase text-blue-900">
                        Rental application
                    </p>
                    <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
                        {isLocked
                            ? "Submitted application"
                            : isTerminalReadOnly
                                ? "Application"
                                : "Complete your application"}
                    </h1>
                    {isReadOnly && (
                        <div className="mt-3">
                            <EquipmentTypeBadge data={data} variant="line" testId="customer-submission-equipment" />
                        </div>
                    )}
                    {/* Phase STATE-DRIFT-SWEEP — surface non-`submitted`
                        terminal states with a plain-language banner. */}
                    {isTerminalReadOnly && (
                        <div
                            className="mt-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 inline-flex items-center gap-2"
                            data-testid="submission-readonly-banner"
                        >
                            <Info size={14} className="text-slate-500" />
                            {editability.reason}
                        </div>
                    )}
                </div>
                {!isReadOnly && (
                    <div className="flex gap-2">
                        <Button
                            variant="outline"
                            onClick={onSaveExit}
                            disabled={saving}
                            data-testid="save-and-exit"
                            className="gap-1.5"
                        >
                            <Save size={14} /> Save & exit
                        </Button>
                    </div>
                )}
            </div>

            {/* Returning-customer prefill banner */}
            {!isReadOnly && prefillInfo?.available && !prefillBannerHidden && (
                <div className="mt-6 rounded-md border border-blue-200 bg-blue-50 p-4 flex items-start gap-3" data-testid="prefill-banner">
                    <RefreshCcw size={18} className="text-blue-900 shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-blue-900">
                            We have your previously filled information for <strong>{prefillInfo.company_name || "this customer"}</strong>.
                        </p>
                        <p className="text-xs text-blue-900/70 mt-0.5">
                            Use it to prefill this application? You can still edit anything before submitting.
                        </p>
                        <div className="mt-3 flex flex-wrap gap-2">
                            <Button
                                size="sm"
                                onClick={onUsePrevious}
                                disabled={applyingPrefill}
                                className="bg-blue-900 hover:bg-blue-800"
                                data-testid="prefill-use-previous"
                            >
                                {applyingPrefill ? "Applying…" : "Use previous info"}
                            </Button>
                            <Button size="sm" variant="outline" onClick={onStartFresh} data-testid="prefill-start-fresh">
                                Start fresh
                            </Button>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onStartFresh}
                        className="text-blue-900/60 hover:text-blue-900"
                        aria-label="Dismiss"
                        data-testid="prefill-dismiss"
                    >
                        <X size={16} />
                    </button>
                </div>
            )}

            {/* Stepper */}
            <ol className="mt-8 flex flex-wrap gap-2 text-xs" data-testid="submission-stepper">
                {STEPS.map((s, i) => {
                    const active = i === stepIdx;
                    const done = i < stepIdx;
                    return (
                        <li key={s.key}>
                            <button
                                type="button"
                                onClick={() => goStep(i)}
                                data-testid={`step-${s.key}`}
                                className={`px-3 py-1.5 rounded-full font-medium transition-colors ${
                                    active
                                        ? "bg-blue-900 text-white"
                                        : done
                                          ? "bg-blue-100 text-blue-900"
                                          : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                                }`}
                            >
                                {i + 1}. {s.label}
                            </button>
                        </li>
                    );
                })}
            </ol>

            {/* Step content */}
            <Card className="mt-6">
                <CardContent className="p-6 space-y-6">
                    {/* Inline error-summary banner (Task 1) — appears at
                        the top of the active step whenever the backend's
                        partial-save 400 left per-field errors on screen.
                        role="alert" + aria-live="polite" so screen
                        readers announce it. Hidden once errors are all
                        cleared (the toast remains for transient ack). */}
                    {!isLocked && errorCount > 0 && (
                        <div
                            role="alert"
                            aria-live="polite"
                            data-testid="field-error-summary-banner"
                            className="rounded-md border border-red-200 bg-red-50 p-3 flex items-start gap-2.5 text-sm text-red-800"
                        >
                            <AlertCircle size={16} className="shrink-0 mt-0.5 text-red-600" />
                            <div className="flex-1 min-w-0">
                                <p className="font-medium">
                                    <span data-testid="field-error-summary-count">{errorCount}</span>{" "}
                                    {errorCount === 1 ? "field needs" : "fields need"} attention.
                                </p>
                                <p className="text-xs text-red-700/80 mt-0.5">
                                    {formatErrorSummaryLabel(errorCount)} before this draft can save cleanly.
                                </p>
                                {(() => {
                                    // Sub-line surfaces the partial-save split
                                    // — "{N} saved, {M} failed" — so customers
                                    // see at a glance that their valid fields
                                    // WERE persisted. Helper returns null when
                                    // saved_keys is absent so we never render
                                    // a bogus "undefined saved" line.
                                    const sub = formatSavedFailedSubline(
                                        savedKeysSnapshot,
                                        errorCount
                                    );
                                    if (!sub) return null;
                                    return (
                                        <p
                                            data-testid="field-error-summary-saved-line"
                                            className="text-xs text-red-700/80 mt-0.5"
                                        >
                                            {sub}
                                        </p>
                                    );
                                })()}
                            </div>
                            <button
                                type="button"
                                onClick={jumpToFirstError}
                                data-testid="field-error-summary-jump"
                                className="text-sm font-medium text-red-700 hover:text-red-900 underline underline-offset-2 shrink-0"
                            >
                                Jump to first
                            </button>
                        </div>
                    )}

                    {currentStep.key === "equipment" && (
                        <div className="space-y-4" data-testid="equipment-step">
                            <div>
                                <h2 className="font-display text-lg font-semibold text-slate-900">What are you renting?</h2>
                                <p className="text-sm text-slate-600 mt-1">Pick the equipment type for this rental application.</p>
                            </div>
                            {(() => {
                                const eqField = catalog.fields.find((f) => f.key === "equipment_type");
                                if (!eqField || cfg[eqField.key] === "hidden") {
                                    return <p className="text-sm text-slate-500">Equipment selection is not required for this invite.</p>;
                                }
                                return (
                                    <EquipmentTypeCards
                                        value={data.equipment_type}
                                        onChange={(v) => setField("equipment_type", v)}
                                        options={eqField.options}
                                        otherText={data.equipment_type_other}
                                        onOtherTextChange={(v) => setField("equipment_type_other", v)}
                                        disabled={isReadOnly}
                                    />
                                );
                            })()}
                        </div>
                    )}

                    {/* Task 25 — exclude ``signatures`` here too. Previously
                        the generic field-row loop fell through on the
                        signatures step and rendered each signature field
                        as a default ``<Input>`` (because FieldRenderer has
                        no ``type === "signature"`` branch), producing two
                        empty required text boxes ABOVE the real signature
                        pads — customers couldn't tell what to type and
                        couldn't submit. The proper SignaturePad widgets
                        render in the dedicated ``signatures``-step block
                        below (line ~673). */}
                    {currentStep.key !== "equipment" && currentStep.key !== "review" && currentStep.key !== "uploads" && currentStep.key !== "signatures" && (
                        <div className="space-y-5">
                            {(fieldsByGroup[groupKey] || []).map(renderFieldRow)}
                            {(!fieldsByGroup[groupKey] || fieldsByGroup[groupKey].length === 0) && (
                                <p className="text-sm text-slate-500">No fields in this section for this invite.</p>
                            )}
                        </div>
                    )}

                    {currentStep.key === "uploads" && (
                        <div className="space-y-4">
                            {(fieldsByGroup.uploads || []).map((f) => {
                                const required = cfg[f.key] === "required";
                                const prefillMatch = (prefillInfo?.uploads || []).find((u) => u.field_key === f.key);
                                const haveCurrent = !!uploadsByKey[f.key];
                                const showStaleWarning = prefillMatch?.must_replace && haveCurrent && f.key === "coi_upload";
                                const currentUpload = uploadsByKey[f.key];
                                // Task 26-B — inline error if this field is required + missing
                                // + the user has already tried to submit at least once.
                                const isMissing = missingUploadKeys.has(f.key);
                                return (
                                    <div
                                        key={f.key}
                                        data-testid={`customer-upload-row-${f.key}`}
                                        className={isMissing ? "rounded-md border border-red-300 bg-red-50/40 p-3 -m-3" : undefined}
                                    >
                                        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                                            <Label className="block">
                                                {f.label}
                                                {required && <span className="text-red-600 ml-1">*</span>}
                                            </Label>
                                            <div className="flex items-center gap-2">
                                                {currentUpload && (
                                                    <UploadStatusBadge
                                                        status={currentUpload.status}
                                                        reason={currentUpload.reason}
                                                        fieldKey={f.key}
                                                    />
                                                )}
                                                {currentUpload && (
                                                    <Button
                                                        type="button"
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => setPreviewFile(currentUpload)}
                                                        data-testid={`customer-preview-upload-${f.key}`}
                                                        className="gap-1.5"
                                                    >
                                                        <Eye size={12} /> Preview
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                        {showStaleWarning && (
                                            <div className="mb-2 rounded-md border border-amber-200 bg-amber-50 p-3 flex items-start gap-2 text-xs text-amber-900" data-testid={`stale-upload-${f.key}`}>
                                                <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                                                <span><strong>Action required:</strong> this document is older than 12 months — please re-upload a current COI.</span>
                                            </div>
                                        )}
                                        <FileUploadField
                                            field={f}
                                            submissionId={id}
                                            currentUpload={currentUpload}
                                            onChange={onUploadChange}
                                            disabled={isReadOnly}
                                        />
                                        {isMissing && (
                                            <p
                                                className="mt-1.5 text-xs font-medium text-red-700"
                                                data-testid={`upload-error-${f.key}`}
                                            >
                                                {f.label} is required.
                                            </p>
                                        )}
                                    </div>
                                );
                            })}
                            {(!fieldsByGroup.uploads || fieldsByGroup.uploads.length === 0) && (
                                <p className="text-sm text-slate-500">No uploads required for this invite.</p>
                            )}
                        </div>
                    )}

                    {currentStep.key === "signatures" && (
                        <div className="space-y-4">
                            <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700" data-testid="signature-freshness-note">
                                Fresh signature required for every application — previously saved signatures are never carried over.
                            </div>
                            {(fieldsByGroup.signatures || []).map((f) => (
                                <SignaturePad
                                    key={f.key}
                                    field={f}
                                    submissionId={id}
                                    defaultSignerName={
                                        f.key === "signature_guarantor"
                                            ? data.guarantor_name || ""
                                            : submission?.customer_name || ""
                                    }
                                    currentSignature={signaturesByKey[f.key]}
                                    onSaved={async () => {
                                        await refreshSubmission();
                                    }}
                                    disabled={isReadOnly}
                                />
                            ))}
                            {(!fieldsByGroup.signatures || fieldsByGroup.signatures.length === 0) && (
                                <p className="text-sm text-slate-500">No signatures required for this invite.</p>
                            )}
                        </div>
                    )}

                    {currentStep.key === "review" && (
                        <div className="space-y-6" data-testid="review-step">
                            {isLocked ? (
                                <div className="rounded-md border border-emerald-100 bg-emerald-50 p-4 flex items-start gap-3" data-testid="review-locked-banner">
                                    <CheckCircle2 size={18} className="text-emerald-700 shrink-0 mt-0.5" />
                                    <div className="text-sm text-emerald-900 flex-1">
                                        <p className="font-medium">
                                            {submission.status === "approved"
                                                ? "Application approved"
                                                : submission.status === "rejected"
                                                  ? "Application rejected"
                                                  : "Application submitted"}
                                        </p>
                                        <p className="text-emerald-800/80 mt-0.5">
                                            {submission.status === "approved"
                                                ? "Your application has been approved by the RentFlow team."
                                                : submission.status === "rejected"
                                                  ? "Your application was rejected. See the reviewer note below."
                                                  : "We've notified the RentFlow team. They'll be in touch shortly."}
                                        </p>
                                        {submission.review_note && (
                                            <p className="mt-2 px-3 py-2 rounded bg-white border border-emerald-100 whitespace-pre-wrap text-emerald-900 text-xs">
                                                <strong>Reviewer note:</strong> {submission.review_note}
                                            </p>
                                        )}
                                    </div>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={async () => {
                                            try {
                                                const resp = await api.get(`/submissions/${id}/pdf`, { responseType: "blob" });
                                                const url = URL.createObjectURL(resp.data);
                                                const a = document.createElement("a");
                                                a.href = url;
                                                a.download = `submission-${id}.pdf`;
                                                a.click();
                                                URL.revokeObjectURL(url);
                                            } catch (err) {
                                                toast.error(formatApiError(err));
                                            }
                                        }}
                                        data-testid="customer-download-pdf"
                                        className="gap-1.5"
                                    >
                                        <Download size={14} /> PDF
                                    </Button>
                                </div>
                            ) : (
                                <>
                                    {displayMissingCount === 0 ? (
                                        <div className="rounded-md border border-emerald-100 bg-emerald-50 p-4 flex items-start gap-3" data-testid="review-ready">
                                            <CheckCircle2 size={18} className="text-emerald-700 shrink-0 mt-0.5" />
                                            <div className="text-sm text-emerald-900">
                                                <p className="font-medium">{dryRun?.summary || "All required fields complete."}</p>
                                                <p className="text-emerald-800/80 mt-0.5">Review your details below, then submit when ready.</p>
                                                {dryRun?.offline && (
                                                    <p className="text-amber-800 mt-2 text-xs" data-testid="dryrun-offline-warning">
                                                        Validation server unreachable — submit may still reject.
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="rounded-md border border-amber-100 bg-amber-50 p-4 flex items-start gap-3" data-testid="review-incomplete">
                                            <AlertCircle size={18} className="text-amber-700 shrink-0 mt-0.5" />
                                            <div className="text-sm text-amber-900">
                                                <p className="font-medium" data-testid="dryrun-summary">{displayMissingCount} required field(s) still need attention:</p>
                                                <ul className="list-disc ml-5 mt-2 space-y-0.5">
                                                    {displayMissingFields.map((f) => (
                                                        <li key={f.key} data-testid={`dryrun-missing-${f.key}`}>
                                                            {f.label}
                                                            {f.errorMessage && <span className="text-amber-700"> — {f.errorMessage}</span>}
                                                        </li>
                                                    ))}
                                                </ul>
                                                {dryRun?.offline && (
                                                    <p className="text-amber-700 mt-2 text-xs" data-testid="dryrun-offline-warning">
                                                        Validation server unreachable — list shown from local check; submit may still reject.
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}

                            <div className="rounded-md border border-slate-200 divide-y divide-slate-100">
                                {catalog.fields
                                    .filter((f) => cfg[f.key] !== "hidden")
                                    .map((f) => {
                                        let val = "—";
                                        if (f.type === "file") {
                                            val = uploadsByKey[f.key]?.filename || "—";
                                        } else if (f.type === "signature") {
                                            const s = signaturesByKey[f.key];
                                            if (s) val = `${s.signer_name} · ${s.method}`;
                                            else val = "—";
                                        } else if (f.type === "composite") {
                                            const v = data[f.key];
                                            if (v && typeof v === "object") {
                                                val = f.subfields
                                                    .map((s) => v[s.key])
                                                    .filter(Boolean)
                                                    .join(" · ") || "—";
                                            }
                                        } else if (f.type === "enum") {
                                            const opt = (f.options || []).find((o) => o.value === data[f.key]);
                                            val = opt ? (opt.value === "other" ? (data.equipment_type_other || opt.label) : opt.label) : (data[f.key] || "—");
                                        } else {
                                            val = data[f.key] || "—";
                                        }
                                        return (
                                            <div key={f.key} className="px-4 py-2.5 flex justify-between gap-4 text-sm" data-testid={`review-row-${f.key}`}>
                                                <span className="text-slate-500">
                                                    {f.label}
                                                    {cfg[f.key] === "required" && <span className="text-red-500 ml-1">*</span>}
                                                </span>
                                                <span className="text-slate-900 text-right break-words max-w-[60%]">{val}</span>
                                            </div>
                                        );
                                    })}
                            </div>

                            {submitErrors.length > 0 && (
                                <div className="rounded-md border border-red-100 bg-red-50 p-4 text-sm text-red-700" data-testid="submit-errors">
                                    <p className="font-medium mb-1">Please fix the following before submitting:</p>
                                    <ul className="list-disc ml-5">
                                        {submitErrors.map((m, i) => (
                                            <li key={i}>{m}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Footer nav */}
            <div className="mt-6 flex flex-wrap gap-3 justify-between items-center">
                <Button
                    variant="outline"
                    onClick={() => goStep(stepIdx - 1)}
                    disabled={stepIdx === 0}
                    data-testid="step-prev"
                    className="gap-1.5"
                >
                    <ChevronLeft size={14} /> Back
                </Button>

                <div className="text-xs text-slate-500" data-testid="autosave-indicator">
                    {saving ? "Saving…" : isLocked ? "Locked" : "Auto-saving every 20s"}
                </div>

                {stepIdx < STEPS.length - 1 ? (
                    <Button
                        onClick={() => goStep(stepIdx + 1, { gated: true })}
                        data-testid="step-next"
                        className="bg-blue-900 hover:bg-blue-800 gap-1.5"
                    >
                        Next <ChevronRight size={14} />
                    </Button>
                ) : (
                    !isLocked && (
                        <Button
                            onClick={onSubmitFinal}
                            // Task 26-B — keep Submit enabled when ONLY required
                            // uploads are missing so the inline-error UX can fire.
                            // Non-upload required fields (text/signature) still
                            // block submission.
                            disabled={submitting || (
                                useServerSummary
                                    ? !serverOk && displayMissingFields.some((f) => f.type !== "file")
                                    : requiredMissing.some((f) => f.type !== "file")
                            )}
                            data-testid="submit-application-button"
                            className="bg-blue-900 hover:bg-blue-800 gap-1.5 disabled:opacity-50"
                        >
                            {submitting ? "Submitting…" : (
                                <>
                                    <Send size={14} /> Submit application
                                </>
                            )}
                        </Button>
                    )
                )}
            </div>

            <FilePreviewModal
                open={previewOpen}
                onOpenChange={(o) => !o && setPreviewFile(null)}
                file={previewFile}
                submissionId={id}
                mode="customer"
            />
        </DashboardShell>
    );
}
